/**
 * Generic Spanish City Landing Page
 * Handles all /es/:city routes that don't have a dedicated page.
 * City data is resolved from the CITY_DATA map below.
 */
import { useParams, Navigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { StickyBanner } from "@/components/StickyBanner";
import { SEO } from "@/components/SEO";
import { SpanishHero } from "@/components/SpanishHero";
import { SpanishWhyUs } from "@/components/SpanishWhyUs";
import { SpanishServices } from "@/components/SpanishServices";
import { SpanishTestimonials } from "@/components/SpanishTestimonials";
import { SpanishFAQ } from "@/components/SpanishFAQ";
import { SpanishFinalCTA } from "@/components/SpanishFinalCTA";
import { SpanishReferFriend } from "@/components/SpanishReferFriend";
import { LocationContext } from "@/lib/locations";

import { Guarantee } from "@/components/Guarantee";
import { PricingCalculator } from "@/components/PricingCalculator";
import { FrequencyDiscounts } from "@/components/FrequencyDiscounts";
import { REGIONS } from "@/lib/locations";

// Map from URL slug → city display name + region info
// This covers ALL remaining English-only cities that need Spanish pages
const CITY_DATA: Record<string, { name: string; regionSlug: keyof typeof REGIONS; enPath: string }> = {
  // Sacramento Area
  "folsom":            { name: "Folsom",            regionSlug: "sacramento-area", enPath: "/locations/folsom" },
  "elk-grove":         { name: "Elk Grove",         regionSlug: "sacramento-area", enPath: "/locations/elk-grove" },
  "davis":             { name: "Davis",             regionSlug: "sacramento-area", enPath: "/locations/davis" },
  "rocklin":           { name: "Rocklin",           regionSlug: "sacramento-area", enPath: "/locations/rocklin" },
  "granite-bay":       { name: "Granite Bay",       regionSlug: "sacramento-area", enPath: "/locations/granite-bay" },
  "el-dorado-hills":   { name: "El Dorado Hills",   regionSlug: "sacramento-area", enPath: "/locations/el-dorado-hills" },
  "west-sacramento":   { name: "West Sacramento",   regionSlug: "sacramento-area", enPath: "/locations/west-sacramento" },
  "rancho-cordova":    { name: "Rancho Cordova",    regionSlug: "sacramento-area", enPath: "/locations/rancho-cordova" },
  "carmichael":        { name: "Carmichael",        regionSlug: "sacramento-area", enPath: "/locations/carmichael" },
  "citrus-heights":    { name: "Citrus Heights",    regionSlug: "sacramento-area", enPath: "/locations/citrus-heights" },

  // Contra Costa County
  "walnut-creek":      { name: "Walnut Creek",      regionSlug: "contra-costa", enPath: "/locations/walnut-creek" },
  "concord":           { name: "Concord",           regionSlug: "contra-costa", enPath: "/locations/concord" },
  "richmond":          { name: "Richmond",          regionSlug: "contra-costa", enPath: "/locations/richmond" },
  "antioch":           { name: "Antioch",           regionSlug: "contra-costa", enPath: "/locations/antioch" },
  "danville":          { name: "Danville",          regionSlug: "contra-costa", enPath: "/locations/danville" },
  "brentwood-ca":      { name: "Brentwood",         regionSlug: "contra-costa", enPath: "/locations/brentwood-ca" },
  "martinez":          { name: "Martinez",          regionSlug: "contra-costa", enPath: "/locations/martinez" },
  "pleasant-hill":     { name: "Pleasant Hill",     regionSlug: "contra-costa", enPath: "/locations/pleasant-hill" },
  "lafayette":         { name: "Lafayette",         regionSlug: "contra-costa", enPath: "/locations/lafayette" },

  // Solano County
  "suisun-city":       { name: "Suisun City",       regionSlug: "solano", enPath: "/locations/suisun-city" },
  "dixon":             { name: "Dixon",             regionSlug: "solano", enPath: "/locations/dixon" },

  // San Diego Area
  "carlsbad":          { name: "Carlsbad",          regionSlug: "san-diego", enPath: "/locations/carlsbad" },
  "oceanside":         { name: "Oceanside",         regionSlug: "san-diego", enPath: "/locations/oceanside" },
  "chula-vista":       { name: "Chula Vista",       regionSlug: "san-diego", enPath: "/locations/chula-vista" },
  "escondido":         { name: "Escondido",         regionSlug: "san-diego", enPath: "/locations/escondido" },
  "san-marcos":        { name: "San Marcos",        regionSlug: "san-diego", enPath: "/locations/san-marcos" },
  "encinitas":         { name: "Encinitas",         regionSlug: "san-diego", enPath: "/locations/encinitas" },
  "el-cajon":          { name: "El Cajon",          regionSlug: "san-diego", enPath: "/locations/el-cajon" },
  "vista":             { name: "Vista",             regionSlug: "san-diego", enPath: "/locations/vista" },
  "coronado":          { name: "Coronado",          regionSlug: "san-diego", enPath: "/locations/coronado" },

  // Orange County
  "costa-mesa":        { name: "Costa Mesa",        regionSlug: "orange-county", enPath: "/locations/costa-mesa" },
  "mission-viejo":     { name: "Mission Viejo",     regionSlug: "orange-county", enPath: "/locations/mission-viejo" },
  "fullerton":         { name: "Fullerton",         regionSlug: "orange-county", enPath: "/locations/fullerton" },
  "orange":            { name: "Orange",            regionSlug: "orange-county", enPath: "/locations/orange" },
  "san-clemente":      { name: "San Clemente",      regionSlug: "orange-county", enPath: "/locations/san-clemente" },

  // Santa Clara County
  "santa-clara-city":  { name: "Santa Clara",       regionSlug: "santa-clara", enPath: "/locations/santa-clara" },
  "sunnyvale":         { name: "Sunnyvale",         regionSlug: "santa-clara", enPath: "/locations/sunnyvale" },
  "mountain-view":     { name: "Mountain View",     regionSlug: "santa-clara", enPath: "/locations/mountain-view" },
  "palo-alto":         { name: "Palo Alto",         regionSlug: "santa-clara", enPath: "/locations/palo-alto" },
  "cupertino":         { name: "Cupertino",         regionSlug: "santa-clara", enPath: "/locations/cupertino" },
  "milpitas":          { name: "Milpitas",          regionSlug: "santa-clara", enPath: "/locations/milpitas" },
  "los-gatos":         { name: "Los Gatos",         regionSlug: "santa-clara", enPath: "/locations/los-gatos" },
  "morgan-hill":       { name: "Morgan Hill",       regionSlug: "santa-clara", enPath: "/locations/morgan-hill" },
  "gilroy":            { name: "Gilroy",            regionSlug: "santa-clara", enPath: "/locations/gilroy" },

  // Marin County
  "novato":            { name: "Novato",            regionSlug: "marin", enPath: "/locations/novato" },
  "mill-valley":       { name: "Mill Valley",       regionSlug: "marin", enPath: "/locations/mill-valley" },
  "san-anselmo":       { name: "San Anselmo",       regionSlug: "marin", enPath: "/locations/san-anselmo" },
  "corte-madera":      { name: "Corte Madera",      regionSlug: "marin", enPath: "/locations/corte-madera" },
  "tiburon":           { name: "Tiburón",           regionSlug: "marin", enPath: "/locations/tiburon" },
  "sausalito":         { name: "Sausalito",         regionSlug: "marin", enPath: "/locations/sausalito" },
  "larkspur":          { name: "Larkspur",          regionSlug: "marin", enPath: "/locations/larkspur" },
  "fairfax":           { name: "Fairfax",           regionSlug: "marin", enPath: "/locations/fairfax" },
  "san-rafael":        { name: "San Rafael",        regionSlug: "marin", enPath: "/locations/san-rafael" },

  // San Francisco Area
  "daly-city":         { name: "Daly City",         regionSlug: "san-francisco", enPath: "/locations/daly-city" },
  "south-san-francisco": { name: "South San Francisco", regionSlug: "san-francisco", enPath: "/locations/south-san-francisco" },
  "san-bruno":         { name: "San Bruno",         regionSlug: "san-francisco", enPath: "/locations/san-bruno" },
  "pacifica":          { name: "Pacifica",          regionSlug: "san-francisco", enPath: "/locations/pacifica" },
  "burlingame":        { name: "Burlingame",        regionSlug: "san-francisco", enPath: "/locations/burlingame" },
  "millbrae":          { name: "Millbrae",          regionSlug: "san-francisco", enPath: "/locations/millbrae" },
  "san-mateo":         { name: "San Mateo",         regionSlug: "san-francisco", enPath: "/locations/san-mateo" },

  // Los Angeles — individual cities not covered by sub-region pages
  "santa-monica":      { name: "Santa Monica",      regionSlug: "los-angeles", enPath: "/locations/la/la-westside" },
  "venice":            { name: "Venice",            regionSlug: "los-angeles", enPath: "/locations/la/la-westside" },
  "brentwood-los-angeles": { name: "Brentwood",     regionSlug: "los-angeles", enPath: "/locations/la/la-westside" },
  "culver-city":       { name: "Culver City",       regionSlug: "los-angeles", enPath: "/locations/la/la-westside" },
  "sherman-oaks":      { name: "Sherman Oaks",      regionSlug: "los-angeles", enPath: "/locations/la/la-san-fernando-valley" },
  "encino":            { name: "Encino",            regionSlug: "los-angeles", enPath: "/locations/la/la-san-fernando-valley" },
  "studio-city":       { name: "Studio City",       regionSlug: "los-angeles", enPath: "/locations/la/la-san-fernando-valley" },
  "woodland-hills":    { name: "Woodland Hills",    regionSlug: "los-angeles", enPath: "/locations/la/la-san-fernando-valley" },
  "porter-ranch":      { name: "Porter Ranch",      regionSlug: "los-angeles", enPath: "/locations/la/la-san-fernando-valley" },
  "arcadia":           { name: "Arcadia",           regionSlug: "los-angeles", enPath: "/locations/la/la-san-gabriel-valley" },
  "san-marino":        { name: "San Marino",        regionSlug: "los-angeles", enPath: "/locations/la/la-san-gabriel-valley" },
  "monrovia":          { name: "Monrovia",          regionSlug: "los-angeles", enPath: "/locations/la/la-san-gabriel-valley" },
  "manhattan-beach":   { name: "Manhattan Beach",   regionSlug: "los-angeles", enPath: "/locations/la/la-south-bay" },
  "redondo-beach":     { name: "Redondo Beach",     regionSlug: "los-angeles", enPath: "/locations/la/la-south-bay" },
  "torrance":          { name: "Torrance",          regionSlug: "los-angeles", enPath: "/locations/la/la-south-bay" },
  "el-segundo":        { name: "El Segundo",        regionSlug: "los-angeles", enPath: "/locations/la/la-south-bay" },

  // Alameda County
  "union-city":        { name: "Union City",        regionSlug: "alameda", enPath: "/locations/union-city" },
  "newark":            { name: "Newark",            regionSlug: "alameda", enPath: "/locations/newark" },
  "alameda-city":      { name: "Alameda",           regionSlug: "alameda", enPath: "/locations/alameda" },
};

// Region-level testimonials mapping for generic cities
const REGION_TESTIMONIALS: Record<string, "sacramento" | "la" | "san-diego" | "san-francisco" | "irvine" | "oakland" | "contra-costa" | "solano" | "alameda" | "orange-county" | "santa-clara" | "marin"> = {
  "sacramento-area": "sacramento",
  "los-angeles": "la",
  "san-diego": "san-diego",
  "san-francisco": "san-francisco",
  "orange-county": "orange-county",
  "santa-clara": "santa-clara",
  "marin": "marin",
  "contra-costa": "contra-costa",
  "solano": "solano",
  "alameda": "alameda",
};

const SpanishCityPage = () => {
  const { citySlug } = useParams<{ citySlug: string }>();
  const cityData = citySlug ? CITY_DATA[citySlug] : null;

  if (!cityData) return <Navigate to="/es/sacramento" replace />;

  const { name, regionSlug, enPath } = cityData;
  const testimonialRegion = REGION_TESTIMONIALS[regionSlug] || "sacramento";
  const regionName = REGIONS[regionSlug]?.name || "California";

  return (
    <LocationContext.Provider value={{ slug: citySlug!, name, type: "city", regionSlug }}>
      <SEO
        title={`Limpieza del Hogar en ${name} — Uplevel Cleaning`}
        description={`Servicio profesional de limpieza del hogar en ${name}, ${regionName}. Precios planos y transparentes, reserva en 60 segundos, garantía de satisfacción del 200%.`}
        keywords={`limpieza del hogar ${name}, servicio de limpieza ${name}, limpieza profunda ${name}, empleada doméstica ${name}, limpieza de casas ${name} ca, uplevel cleaning ${name}`}
        url={`https://uplevelcleaning.com/es/${citySlug}`}
        alternateUrl={`https://uplevelcleaning.com${enPath}`}
        alternateLang="en-US"
        locationName={name}
        regionSlug={regionSlug}
        lang="es"
        includeLocalBusiness={true}
        includeFAQ={true}
        breadcrumbs={[
          { name: "Inicio", url: "/" },
          { name: "En Español", url: "/es/sacramento" },
          { name: name, url: `/es/${citySlug}` }
        ]}
        reviews={[
          { author: "Cliente Local", rating: 5, reviewBody: `Uplevel Cleaning es el mejor servicio de limpieza en ${name}. Puntuales, profesionales y resultados impecables.`, datePublished: "2026-03-01" },
          { author: "Propietario Satisfecho", rating: 5, reviewBody: `Llevamos meses usando Uplevel Cleaning en ${name}. Nunca nos decepcionan. ¡Muy recomendados!`, datePublished: "2026-02-15" }
        ]}
      />
      <div className="flex min-h-screen flex-col" lang="es">
        <StickyBanner />
        <Navbar />
        <main className="flex-1">
          <SpanishHero cityName={name} regionSlug={enPath} />
          <SpanishWhyUs cityName={name} />
          <SpanishServices />

          <FrequencyDiscounts />
          <PricingCalculator />
          <SpanishTestimonials region={testimonialRegion as any} />
          <Guarantee />
          <SpanishReferFriend />
          <SpanishFAQ cityName={name} />
          <SpanishFinalCTA cityName={name} />
        </main>
        <Footer />
      </div>
    </LocationContext.Provider>
  );
};

export default SpanishCityPage;
